import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '../components/ui/dialog';
import { formatPrice } from '../lib/utils';
import { 
  Trash2, ShoppingBag, Tag, Sparkles, Save, FolderOpen, 
  ChevronRight, Check, X, Loader2, Plus, Minus, Info, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

export default function Cart() {
  const { cart, removeFromCart, getTotal, updateQuantity, clearCart, setCart } = useCart();
  const { token, user } = useAuth();
  
  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState(null);
  
  // Service detail modal
  const [selectedService, setSelectedService] = useState(null);
  const [serviceLoading, setServiceLoading] = useState(false);
  
  // Recommendations
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);
  
  // Saved carts
  const [savedCarts, setSavedCarts] = useState([]);
  const [showSavedCarts, setShowSavedCarts] = useState(false);
  const [savingCart, setSavingCart] = useState(false);

  // Load recommendations when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      loadRecommendations();
    } else {
      setRecommendations([]);
    }
  }, [cart]);

  const loadRecommendations = async () => {
    setRecsLoading(true);
    try {
      const serviceIds = cart.map(item => item.service_id).join(',');
      const categories = [...new Set(cart.map(item => item.category))].join(',');
      
      const response = await fetch(
        `${BACKEND_URL}/api/cart/recommendations?cart_service_ids=${serviceIds}&cart_categories=${categories}&limit=4`
      );
      
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (err) {
      console.error('Failed to load recommendations:', err);
    } finally {
      setRecsLoading(false);
    }
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setPromoLoading(true);
    setPromoError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/cart/validate-promo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          code: promoCode,
          cart_items: cart
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppliedPromo(data);
        toast.success(`Promo code applied! ${data.description}`);
      } else {
        const err = await response.json();
        setPromoError(err.detail || 'Invalid promo code');
        toast.error(err.detail || 'Invalid promo code');
      }
    } catch (err) {
      setPromoError('Failed to validate promo code');
      toast.error('Failed to validate promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
    setPromoError(null);
  };

  const viewServiceDetails = async (serviceId) => {
    setServiceLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cart/service/${serviceId}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedService(data);
      }
    } catch (err) {
      toast.error('Failed to load service details');
    } finally {
      setServiceLoading(false);
    }
  };

  const saveCartForLater = async () => {
    if (!token) {
      toast.error('Please login to save cart');
      return;
    }
    
    setSavingCart(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/cart/save`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ items: cart })
      });
      
      if (response.ok) {
        toast.success('Cart saved for later!');
      } else {
        toast.error('Failed to save cart');
      }
    } catch (err) {
      toast.error('Failed to save cart');
    } finally {
      setSavingCart(false);
    }
  };

  const loadSavedCarts = async () => {
    if (!token) {
      toast.error('Please login to view saved carts');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/cart/saved`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSavedCarts(data.saved_carts || []);
        setShowSavedCarts(true);
      }
    } catch (err) {
      toast.error('Failed to load saved carts');
    }
  };

  const restoreSavedCart = (savedCart) => {
    setCart(savedCart.items);
    setShowSavedCarts(false);
    toast.success('Cart restored!');
  };

  const deleteSavedCart = async (cartId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/cart/saved/${cartId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSavedCarts(savedCarts.filter(c => c.id !== cartId));
        toast.success('Saved cart deleted');
      }
    } catch (err) {
      toast.error('Failed to delete saved cart');
    }
  };

  // Calculate totals
  const subtotal = getTotal();
  const discount = appliedPromo?.discount_amount || 0;
  const afterDiscount = subtotal - discount;
  const vat = afterDiscount * 0.24;
  const total = afterDiscount + vat;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold mb-2">Shopping Cart</h1>
            <p className="text-muted-foreground">Review your items before checkout</p>
          </div>
          {user && cart.length > 0 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadSavedCarts}>
                <FolderOpen className="w-4 h-4 mr-2" />
                Saved Carts
              </Button>
              <Button variant="outline" size="sm" onClick={saveCartForLater} disabled={savingCart}>
                {savingCart ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save for Later
              </Button>
            </div>
          )}
        </div>

        {cart.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center py-12">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Your cart is empty</h3>
              <p className="text-muted-foreground mb-4">Browse our services to get started</p>
              <div className="flex gap-3 justify-center">
                <Link to="/services">
                  <Button>Browse Services</Button>
                </Link>
                {user && (
                  <Button variant="outline" onClick={loadSavedCarts}>
                    <FolderOpen className="w-4 h-4 mr-2" />
                    View Saved Carts
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item, idx) => (
                <Card key={idx} data-testid={`cart-item-${idx}`} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex">
                      {/* Clickable area for details */}
                      <button
                        onClick={() => viewServiceDetails(item.service_id)}
                        className="flex-1 p-4 text-left hover:bg-slate-50 transition-colors"
                        data-testid={`view-details-${idx}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                            {item.service_name.charAt(0)}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{item.service_name}</h3>
                              <Info className="w-4 h-4 text-muted-foreground" />
                            </div>
                            <p className="text-sm text-muted-foreground mb-1">{item.tier_name}</p>
                            {item.duration_months && (
                              <Badge variant="secondary" className="text-xs">
                                <Clock className="w-3 h-3 mr-1" />
                                {item.duration_months} month{item.duration_months > 1 ? 's' : ''}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                      
                      {/* Price and actions */}
                      <div className="p-4 flex flex-col items-end justify-between border-l bg-slate-50">
                        <p className="text-xl font-bold text-primary">{formatPrice(item.price)}</p>
                        
                        {/* Quantity controls (if applicable) */}
                        {item.allow_quantity && (
                          <div className="flex items-center gap-2 my-2">
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => updateQuantity(idx, Math.max(1, (item.quantity || 1) - 1))}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-medium">{item.quantity || 1}</span>
                            <Button 
                              variant="outline" 
                              size="icon" 
                              className="h-8 w-8"
                              onClick={() => updateQuantity(idx, (item.quantity || 1) + 1)}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromCart(idx)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`remove-item-${idx}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Promo Code Section */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-5 h-5 text-indigo-600" />
                    <h3 className="font-semibold">Promo Code</h3>
                  </div>
                  
                  {appliedPromo ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Check className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-green-800">{appliedPromo.code}</p>
                          <p className="text-sm text-green-600">{appliedPromo.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-green-700">-{formatPrice(appliedPromo.discount_amount)}</span>
                        <Button variant="ghost" size="sm" onClick={removePromo}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                        className={promoError ? 'border-destructive' : ''}
                        data-testid="promo-code-input"
                      />
                      <Button 
                        onClick={applyPromoCode} 
                        disabled={promoLoading || !promoCode.trim()}
                        data-testid="apply-promo-btn"
                      >
                        {promoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                  )}
                  {promoError && !appliedPromo && (
                    <p className="text-sm text-destructive mt-2">{promoError}</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="space-y-4">
              <Card className="sticky top-20">
                <CardHeader>
                  <CardTitle>Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal ({cart.length} item{cart.length !== 1 ? 's' : ''}):</span>
                      <span className="font-semibold">{formatPrice(subtotal)}</span>
                    </div>
                    
                    {appliedPromo && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount ({appliedPromo.code}):</span>
                        <span className="font-semibold">-{formatPrice(discount)}</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>VAT (24%):</span>
                      <span>{formatPrice(vat)}</span>
                    </div>
                    
                    <div className="border-t pt-2 flex justify-between text-lg font-bold">
                      <span>Total:</span>
                      <span className="text-primary">{formatPrice(total)}</span>
                    </div>
                  </div>
                  
                  <Link to="/checkout" state={{ promoCode: appliedPromo?.code, discount: appliedPromo?.discount_amount }}>
                    <Button className="w-full" size="lg" data-testid="proceed-to-checkout-button">
                      Proceed to Checkout
                      <ChevronRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  
                  <Link to="/services">
                    <Button variant="outline" className="w-full">
                      Continue Shopping
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Recommendations Section */}
        {recommendations.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h2 className="text-xl font-semibold">You Might Also Like</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {recommendations.map((service) => (
                <Card key={service.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold mb-3">
                      {service.name.charAt(0)}
                    </div>
                    <h3 className="font-semibold mb-1 line-clamp-1">{service.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{service.short_description}</p>
                    <Badge variant="secondary" className="mb-3 text-xs">
                      {service.recommendation_reason}
                    </Badge>
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-primary">
                        {formatPrice(Math.min(...(service.pricing_tiers || []).map(t => t.price) || [0]))}
                      </span>
                      <Link to={`/services/${service.id}`}>
                        <Button size="sm" variant="outline">View</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Service Detail Modal */}
        <Dialog open={!!selectedService} onOpenChange={() => setSelectedService(null)}>
          <DialogContent className="max-w-2xl">
            {serviceLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : selectedService && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl">{selectedService.name}</DialogTitle>
                  <DialogDescription>{selectedService.short_description}</DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4">
                  {selectedService.image_url && (
                    <img 
                      src={selectedService.image_url} 
                      alt={selectedService.name}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  )}
                  
                  <div className="prose prose-sm max-w-none">
                    <p>{selectedService.description}</p>
                  </div>
                  
                  {selectedService.features && selectedService.features.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Features:</h4>
                      <ul className="space-y-1">
                        {selectedService.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-600" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {selectedService.pricing_tiers && (
                    <div>
                      <h4 className="font-semibold mb-2">Pricing Options:</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {selectedService.pricing_tiers.map((tier, idx) => (
                          <div key={idx} className="p-3 border rounded-lg">
                            <p className="font-medium">{tier.name}</p>
                            <p className="text-primary font-bold">{formatPrice(tier.price)}</p>
                            {tier.duration_months && (
                              <p className="text-xs text-muted-foreground">{tier.duration_months} months</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-4">
                    <Link to={`/services/${selectedService.id}`} className="flex-1">
                      <Button className="w-full">View Full Details</Button>
                    </Link>
                    <Button variant="outline" onClick={() => setSelectedService(null)}>
                      Close
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Saved Carts Modal */}
        <Dialog open={showSavedCarts} onOpenChange={setShowSavedCarts}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Saved Carts</DialogTitle>
              <DialogDescription>Restore a previously saved cart</DialogDescription>
            </DialogHeader>
            
            {savedCarts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Save className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No saved carts found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {savedCarts.map((savedCart) => (
                  <div key={savedCart.id} className="p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{savedCart.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {savedCart.items.length} item{savedCart.items.length !== 1 ? 's' : ''} • {formatPrice(savedCart.total)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Saved {new Date(savedCart.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => restoreSavedCart(savedCart)}>
                          Restore
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => deleteSavedCart(savedCart.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
